package br.com.vvrefeicoes.portal.feature.shell

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.staticCompositionLocalOf
import br.com.vvrefeicoes.portal.core.storage.KeyValueStorage
import br.com.vvrefeicoes.portal.core.storage.createKeyValueStorage
import br.com.vvrefeicoes.portal.domain.model.CompanyRef
import br.com.vvrefeicoes.portal.domain.model.OrgChartData
import br.com.vvrefeicoes.portal.domain.model.companyNamesOfDirector
import br.com.vvrefeicoes.portal.domain.model.companyNamesOfManager
import br.com.vvrefeicoes.portal.domain.model.directorNamesLinkedTo
import br.com.vvrefeicoes.portal.domain.model.managerNamesLinkedTo
import br.com.vvrefeicoes.portal.domain.model.managerNamesOfDirector

const val ALL_UNITS = "Todas as unidades"
const val ALL_MANAGERS = "Todas as gestões"
const val ALL_DIRECTORS = "Todas as diretorias"

private const val KEY_UNIT = "global.selectedUnit"
private const val KEY_MANAGER = "global.selectedManager"
private const val KEY_DIRECTOR = "global.selectedDirector"

/**
 * Filtros globais do header, compositivos na ordem diretoras → gestoras → unidades:
 * a diretora restringe as gestoras e as unidades; a gestora restringe as unidades.
 */
class GlobalFilters(
    private val storage: KeyValueStorage,
    initialUnit: String,
    initialManager: String,
    initialDirector: String,
) {
    var unit by mutableStateOf(initialUnit)
        private set

    var manager by mutableStateOf(initialManager)
        private set

    var director by mutableStateOf(initialDirector)
        private set

    var units by mutableStateOf<List<CompanyRef>>(emptyList())
        private set

    var managers by mutableStateOf<List<String>>(emptyList())
        private set

    var directors by mutableStateOf<List<String>>(emptyList())
        private set

    private var orgData: OrgChartData? = null
    private var authorizedUnits: List<CompanyRef> = emptyList()

    val selectedUnit: CompanyRef?
        get() = units.firstOrNull { it.name == unit }

    val selectedManager: String?
        get() = manager.takeIf { it != ALL_MANAGERS }

    val selectedDirector: String?
        get() = director.takeIf { it != ALL_DIRECTORS }

    fun selectUnit(value: String) {
        unit = value
        storage.putString(KEY_UNIT, value)
    }

    fun selectManager(value: String) {
        manager = value
        storage.putString(KEY_MANAGER, value)
        if (value == ALL_MANAGERS) resetUnit()
        applyCascade()
    }

    fun selectDirector(value: String) {
        director = value
        storage.putString(KEY_DIRECTOR, value)
        if (value == ALL_DIRECTORS) {
            resetManager()
            resetUnit()
        }
        applyCascade()
    }

    /** Define a fonte (organograma + unidades autorizadas) e recalcula a cascata. */
    fun setSource(
        data: OrgChartData?,
        authorizedUnits: List<CompanyRef>,
    ) {
        orgData = data
        this.authorizedUnits = authorizedUnits
        if (data == null) {
            units = authorizedUnits
            managers = emptyList()
            directors = emptyList()
            autoSelectSingleUnit()
        } else {
            applyCascade()
        }
    }

    private fun applyCascade() {
        val data = orgData ?: return
        val scopeNames = authorizedUnits.map { it.name }.toSet()

        directors = data.directorNamesLinkedTo(scopeNames).sorted()
        if (director != ALL_DIRECTORS && director !in directors) resetDirector()

        val selectedDirector = director.takeIf { it != ALL_DIRECTORS }
        val scopedManagers = data.managerNamesLinkedTo(scopeNames)
        managers =
            (selectedDirector?.let { data.managerNamesOfDirector(it).filter { name -> name in scopedManagers } } ?: scopedManagers)
                .sorted()
        if (manager != ALL_MANAGERS && manager !in managers) resetManager()

        var names = scopeNames
        selectedDirector?.let { names = names.intersect(data.companyNamesOfDirector(it)) }
        manager.takeIf { it != ALL_MANAGERS }?.let { names = names.intersect(data.companyNamesOfManager(it)) }
        units = authorizedUnits.filter { it.name in names }.sortedBy { it.name }
        if (unit != ALL_UNITS && units.none { it.name == unit }) resetUnit()
        autoSelectSingleUnit()
    }

    private fun autoSelectSingleUnit() {
        val single = units.singleOrNull() ?: return
        if (unit != single.name) selectUnit(single.name)
    }

    private fun resetUnit() {
        unit = ALL_UNITS
        storage.putString(KEY_UNIT, ALL_UNITS)
    }

    private fun resetManager() {
        manager = ALL_MANAGERS
        storage.putString(KEY_MANAGER, ALL_MANAGERS)
    }

    private fun resetDirector() {
        director = ALL_DIRECTORS
        storage.putString(KEY_DIRECTOR, ALL_DIRECTORS)
    }
}

@Composable
fun rememberGlobalFilters(): GlobalFilters = remember { globalFiltersFrom(createKeyValueStorage()) }

internal fun globalFiltersFrom(storage: KeyValueStorage): GlobalFilters =
    GlobalFilters(
        storage = storage,
        initialUnit = storage.getString(KEY_UNIT) ?: ALL_UNITS,
        initialManager = storage.getString(KEY_MANAGER) ?: ALL_MANAGERS,
        initialDirector = storage.getString(KEY_DIRECTOR) ?: ALL_DIRECTORS,
    )

val LocalGlobalFilters =
    staticCompositionLocalOf<GlobalFilters> {
        error("GlobalFilters não foi fornecido. Envolva a UI com CompositionLocalProvider(LocalGlobalFilters provides ...).")
    }
